import { useState, type ReactNode } from 'react';
import { CircleAlert, CircleCheck, CircleDashed, CircleDot, Hourglass, LoaderCircle } from 'lucide-react';
import { Trace, TraceCollapsible, TraceCollapsibleContent, TraceCollapsibleTrigger, TraceDescription, TraceIcon, TraceLine,
  TraceTitle } from '@/components/ui/trace';

/**
 * shadcn AI Elements "Task", built on Trace: a piece of work as one line that opens into its steps. The title reads in the
 * present tense while it runs and in the past tense when done; each step says where it stands and what came of it.
 */
export type TaskStepStatus = 'pending' | 'running' | 'waiting' | 'done' | 'skipped' | 'failed';
export interface TaskStepView { id: string; title: string; status: TaskStepStatus; label: string; detail?: ReactNode; action?: ReactNode }
const icons: Record<TaskStepStatus, ReactNode> = { pending: <CircleDashed />, running: <LoaderCircle className="motion-safe:animate-spin" />,
  waiting: <Hourglass />, done: <CircleCheck />, skipped: <CircleDot />, failed: <CircleAlert /> };
const traceStatus = (status: TaskStepStatus) => status === 'running' ? 'running' : status === 'failed' ? 'error'
  : status === 'pending' ? 'pending' : undefined;

export function Task({ title, running, steps, defaultOpen = true, footer }: { title: string; running: boolean; steps: TaskStepView[];
  defaultOpen?: boolean; footer?: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  const failed = steps.some(s => s.status === 'failed');
  return <Trace>
    <TraceCollapsible open={open} onOpenChange={setOpen}>
      <TraceCollapsibleTrigger>
        <TraceLine status={running ? 'running' : failed ? 'error' : undefined}><TraceTitle className="text-foreground">{title}</TraceTitle></TraceLine>
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent>
        <Trace className="gap-3">{steps.map(step => <div key={step.id} className="flex min-w-0 flex-col gap-1">
          <TraceLine status={traceStatus(step.status)} className="max-w-full">
            <TraceIcon>{icons[step.status]}</TraceIcon><TraceTitle className="whitespace-normal">{step.title}</TraceTitle>
            <TraceDescription>{step.label}</TraceDescription>
          </TraceLine>
          {step.detail && <p className="m-0 pl-6 text-xs text-muted-foreground">{step.detail}</p>}
          {step.action && <div className="pl-6">{step.action}</div>}
        </div>)}</Trace>
        {footer && <div className="mt-3">{footer}</div>}
      </TraceCollapsibleContent>
    </TraceCollapsible>
  </Trace>;
}
